/**
 * Utilidad para logging de errores y debugging
 * Ayuda a capturar errores que ocurren en producción
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Log de error con contexto
 */
export const logError = (error, context = {}) => {
  const errorData = {
    timestamp: new Date().toISOString(),
    message: error.message || 'Error desconocido',
    stack: error.stack,
    context,
    userAgent: navigator.userAgent,
    url: window.location.href
  };

  // Siempre loguear en consola
  console.error('🔴 Error capturado:', errorData);

  // En desarrollo, mostrar alerta visual
  if (isDevelopment) {
    console.table(context);
  }

  // Aquí puedes enviar a un servicio de monitoreo
  // Ejemplo: Sentry, LogRocket, etc.
  // sendToMonitoringService(errorData);

  // Guardar en localStorage para debugging (últimos 10 errores)
  try {
    const errors = JSON.parse(localStorage.getItem('app_errors') || '[]');
    errors.unshift(errorData);
    localStorage.setItem('app_errors', JSON.stringify(errors.slice(0, 10)));
  } catch (e) {
    console.warn('No se pudo guardar el error en localStorage', e);
  }

  return errorData;
};

/**
 * Log de información
 */
export const logInfo = (message, data = {}) => {
  if (isDevelopment) {
    console.log(`ℹ️ ${message}`, data);
  }
};

/**
 * Log de advertencia
 */
export const logWarning = (message, data = {}) => {
  console.warn(`⚠️ ${message}`, data);
};

/**
 * Wrapper para funciones async con manejo automático de errores
 */
export const safeAsync = (fn, context = '') => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      logError(error, { context, args });
      throw error; // Re-lanzar para que el componente también lo maneje
    }
  };
};

/**
 * Obtener errores guardados (para debugging)
 */
export const getSavedErrors = () => {
  try {
    return JSON.parse(localStorage.getItem('app_errors') || '[]');
  } catch (e) {
    return [];
  }
};

/**
 * Limpiar errores guardados
 */
export const clearSavedErrors = () => {
  localStorage.removeItem('app_errors');
};

/**
 * Monitorear promesas no manejadas
 */
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    logError(event.reason || new Error('Promise rechazada sin manejar'), {
      type: 'unhandledRejection',
      promise: event.promise
    });
  });
}

export default {
  logError,
  logInfo,
  logWarning,
  safeAsync,
  getSavedErrors,
  clearSavedErrors
};
